/**
 * Pure DOM helpers used by the page-reflow engine and keyboard/caret logic.
 * None of these touch React state — they operate directly on the iframe document.
 */

interface PendingInput {
  inputType: string;
  data: string | null;
}

interface PendingPaste {
  html: string;
  text: string;
}

export function normalizePageBreakSplitRangeStart(splitRange: Range, currentPage: Element): void {
  const sc = splitRange.startContainer;
  const so = splitRange.startOffset;

  if (sc.nodeType === Node.TEXT_NODE) {
    const t = sc as Text;
    if (so !== t.length) return;
    let el: Element | null = t.parentElement;
    while (el && el !== currentPage) {
      if (el.closest('table')) return;
      if (el.parentElement === currentPage) {
        splitRange.setStartAfter(el);
        return;
      }
      el = el.parentElement;
    }
    return;
  }

  if (sc.nodeType === Node.ELEMENT_NODE) {
    const el = sc as Element;
    if (so !== el.childNodes.length) return;
    let walk: Element | null = el;
    while (walk && walk !== currentPage) {
      if (walk.closest('table')) return;
      if (walk.parentElement === currentPage) {
        splitRange.setStartAfter(walk);
        return;
      }
      walk = walk.parentElement;
    }
  }
}

/** Nodes that should be invisible to content operations (page UI chrome). */
function isHelperNode(n: Node | null): boolean {
  if (!n || n.nodeType !== Node.ELEMENT_NODE) return true;
  const cls = typeof (n as Element).className === 'string' ? (n as Element).className : '';
  return cls.includes('__pg-badge__') || cls.includes('__pg-sep__');
}

/** Children of `el` excluding helper/chrome nodes. */
export function contentChildren(el: Element): Element[] {
  return Array.from(el.children).filter(n => !isHelperNode(n));
}

// ---------------------------------------------------------------------------
// Page navigation
// ---------------------------------------------------------------------------

/** Walk siblings to find the next `.page` element (skipping `.__pg-sep__` dividers). */
export function getNextPageElement(page: Element): Element | null {
  let sib = page.nextElementSibling;
  while (sib) {
    if (sib.classList?.contains('page')) return sib;
    if (!sib.classList?.contains('__pg-sep__')) break;
    sib = sib.nextElementSibling;
  }
  return null;
}

export function getPreviousPageElement(page: Element): Element | null {
  let sib = page.previousElementSibling;
  while (sib) {
    if (sib.classList?.contains('page')) return sib;
    if (!sib.classList?.contains('__pg-sep__')) break;
    sib = sib.previousElementSibling;
  }
  return null;
}

export function getPagesStrictlyBetween(doc: Document, earlierPage: Element, laterPage: Element): Element[] {
  const pages = Array.from(doc.querySelectorAll('.page'));
  const i0 = pages.indexOf(earlierPage);
  const i1 = pages.indexOf(laterPage);
  if (i0 < 0 || i1 < 0 || i1 <= i0 + 1) return [];
  return pages.slice(i0 + 1, i1);
}

// ---------------------------------------------------------------------------
// Table editing
// ---------------------------------------------------------------------------

/** Logical column width of a row (sums `colSpan` so one `colspan="4"` cell counts as 4). */
export function getTableRowColumnCount(row: HTMLTableRowElement): number {
  let n = 0;
  const { cells } = row;
  for (let i = 0; i < cells.length; i++) {
    const span = cells[i].colSpan;
    n += span && span > 0 ? span : 1;
  }
  return n;
}

/** Table width in column-units (max over rows). Handles “full-width” placeholder rows with a single colspan cell. */
export function getTableGridColumnCount(table: HTMLTableElement): number {
  let max = 0;
  const { rows } = table;
  for (let i = 0; i < rows.length; i++) {
    max = Math.max(max, getTableRowColumnCount(rows[i]));
  }
  return max;
}

// ---------------------------------------------------------------------------
// Body selection bookmarks (iframe undo restores innerHTML + caret)
// ---------------------------------------------------------------------------

/** While page badges/separators refresh, suppress treating DOM churn as user edits. */
export function setIframeChromeRefreshActive(win: Window | null, active: boolean): void {
  if (!win) return;
  (win as unknown as { __cdccChromeRefresh?: boolean }).__cdccChromeRefresh = active;
}

export function isIframeChromeRefreshActive(win: Window): boolean {
  return !!(win as unknown as { __cdccChromeRefresh?: boolean }).__cdccChromeRefresh;
}

export interface BodySelectionBookmark {
  anchorPath: number[];
  anchorOffset: number;
  focusPath: number[];
  focusOffset: number;
  /**
   * Paths relative to `.page` nodes (stable when body-level `__pg-sep__` siblings change count).
   * Restored first; falls back to `anchorPath` / `focusPath` from `body`.
   */
  rel?: {
    anchorPage: number;
    focusPage: number;
    anchorInPage: number[];
    focusInPage: number[];
  };
}

function pathFromRoot(node: Node, root: HTMLElement): number[] | null {
  if (node === root) return [];
  if (!root.contains(node)) return null;
  const path: number[] = [];
  let cur: Node | null = node;
  while (cur && cur !== root) {
    const p: Node | null = cur.parentNode;
    if (!p) return null;
    const idx = Array.prototype.indexOf.call(p.childNodes, cur);
    if (idx < 0) return null;
    path.unshift(idx);
    cur = p;
  }
  return cur === root ? path : null;
}

function pathFromBody(node: Node, body: HTMLElement): number[] | null {
  return pathFromRoot(node, body);
}

function resolvePathFromRoot(root: HTMLElement, path: number[]): Node | null {
  let cur: Node = root;
  for (let i = 0; i < path.length; i++) {
    const idx = path[i];
    if (idx < 0 || idx >= cur.childNodes.length) return null;
    cur = cur.childNodes[idx];
  }
  return cur;
}

function resolvePathFromBody(body: HTMLElement, path: number[]): Node | null {
  return resolvePathFromRoot(body, path);
}

function clampDomOffset(node: Node, offset: number): number {
  if (node.nodeType === Node.TEXT_NODE) {
    const len = (node as Text).length;
    return Math.max(0, Math.min(offset, len));
  }
  return Math.max(0, Math.min(offset, node.childNodes.length));
}

/** Serialize the current selection as child-index paths from `body` (stable across innerHTML restore). */
export function captureBodySelectionBookmark(win: Window, body: HTMLElement): BodySelectionBookmark | null {
  const sel = win.getSelection();
  if (!sel?.rangeCount) return null;
  const range = sel.getRangeAt(0);
  const doc = win.document;
  const ap = pathFromBody(range.startContainer, body);
  const fp = pathFromBody(range.endContainer, body);
  if (ap == null || fp == null) return null;

  const pages = Array.from(body.querySelectorAll('.page'));
  const apPage = getNodePage(range.startContainer, doc);
  const fpPage = getNodePage(range.endContainer, doc);
  let rel: NonNullable<BodySelectionBookmark['rel']> | undefined;
  if (apPage && fpPage && pages.length) {
    const ai = pages.indexOf(apPage);
    const fi = pages.indexOf(fpPage);
    if (ai >= 0 && fi >= 0) {
      const aIn = pathFromRoot(range.startContainer, apPage as HTMLElement);
      const fIn = pathFromRoot(range.endContainer, fpPage as HTMLElement);
      if (aIn != null && fIn != null) {
        rel = { anchorPage: ai, focusPage: fi, anchorInPage: aIn, focusInPage: fIn };
      }
    }
  }

  return {
    anchorPath: ap,
    anchorOffset: range.startOffset,
    focusPath: fp,
    focusOffset: range.endOffset,
    ...(rel ? { rel } : {}),
  };
}

/** Restore selection after `body.innerHTML` was replaced. */
export function restoreBodySelectionBookmark(win: Window, body: HTMLElement, bookmark: BodySelectionBookmark | null): void {
  const sel = win.getSelection();
  if (!sel) return;
  const doc = win.document;

  const collapseToBodyStart = (): void => {
    try {
      const r = doc.createRange();
      r.setStart(body, 0);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    } catch {
      /* ignore */
    }
  };

  if (!bookmark) {
    collapseToBodyStart();
    return;
  }

  const applyRange = (an: Node, fo: Node): boolean => {
    try {
      const r = doc.createRange();
      r.setStart(an, clampDomOffset(an, bookmark.anchorOffset));
      r.setEnd(fo, clampDomOffset(fo, bookmark.focusOffset));
      sel.removeAllRanges();
      sel.addRange(r);
      return true;
    } catch {
      return false;
    }
  };

  try {
    const rel = bookmark.rel;
    if (rel) {
      const pages = body.querySelectorAll('.page');
      const pa = pages[rel.anchorPage];
      const pf = pages[rel.focusPage];
      if (pa && pf) {
        const an = resolvePathFromRoot(pa as HTMLElement, rel.anchorInPage);
        const fo = resolvePathFromRoot(pf as HTMLElement, rel.focusInPage);
        if (an && fo && applyRange(an, fo)) return;
      }
    }

    const an = resolvePathFromBody(body, bookmark.anchorPath);
    const fo = resolvePathFromBody(body, bookmark.focusPath);
    if (!an || !fo) throw new Error('bookmark path');
    if (!applyRange(an, fo)) throw new Error('bookmark range');
  } catch {
    collapseToBodyStart();
  }
}

/**
 * Scroll so the current selection is visible inside the iframe document only.
 */
export function scrollSelectionIntoView(win: Window): void {
  const sel = win.getSelection();
  if (!sel?.rangeCount) return;
  try {
    const range = sel.getRangeAt(0);
    const sc = range.startContainer;
    const el: Element | null =
      sc.nodeType === Node.TEXT_NODE ? (sc as Text).parentElement : sc.nodeType === Node.ELEMENT_NODE ? (sc as Element) : sc.parentElement;
    if (!el) return;
    const body = win.document.body;
    let scrollTarget: Element = el;
    for (let p: Element | null = el; p && p !== body; p = p.parentElement) {
      const d = win.getComputedStyle(p).display;
      if (d === 'block' || d === 'flex' || d === 'table-cell' || d === 'table-caption' || p.tagName === 'TABLE' || p.tagName === 'TR') {
        scrollTarget = p;
        break;
      }
    }
    scrollTarget.scrollIntoView({ block: 'center', inline: 'nearest' });
  } catch {
    /* ignore */
  }
}

/** Scroll the report `.page` that contains the caret start (helps multi-page + host scroll). */
export function scrollPageContainingSelectionIntoView(win: Window): void {
  const sel = win.getSelection();
  if (!sel?.rangeCount) return;
  try {
    const page = getNodePage(sel.getRangeAt(0).startContainer, win.document);
    (page as HTMLElement | null)?.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  } catch {
    /* ignore */
  }
}

function findHostScrollContainer(start: HTMLElement | null): HTMLElement | null {
  let el: HTMLElement | null = start;
  let fallback: HTMLElement | null = null;
  while (el) {
    const st = getComputedStyle(el);
    const oy = st.overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
      if (el.scrollHeight > el.clientHeight + 1) return el;
      if (!fallback) fallback = el;
    }
    el = el.parentElement;
  }
  return fallback;
}

/** Map iframe-local client rect Y range to the embedding document and scroll the host `overflow-auto` panel. */
export function scrollIframeCaretIntoHostView(iframe: HTMLIFrameElement): void {
  const win = iframe.contentWindow;
  if (!win) return;
  const sel = win.getSelection();
  if (!sel?.rangeCount) return;

  try {
    const range = sel.getRangeAt(0);
    const rects = range.getClientRects();
    let box: { top: number; bottom: number; left: number; right: number } | null = null;

    if (rects.length > 0) {
      box = {
        top: rects[0].top,
        bottom: rects[rects.length - 1].bottom,
        left: rects[0].left,
        right: rects[rects.length - 1].right,
      };
    } else {
      const sc = range.startContainer;
      let probe: Element | null =
        sc.nodeType === Node.TEXT_NODE
          ? (sc as Text).parentElement
          : sc.nodeType === Node.ELEMENT_NODE
            ? (sc as Element)
            : sc.parentElement;
      while (probe && probe !== win.document.body) {
        const br = probe.getBoundingClientRect();
        if (br.height > 0 || br.width > 0) {
          box = { top: br.top, bottom: br.bottom, left: br.left, right: br.right };
          break;
        }
        probe = probe.parentElement;
      }
      if (!box) {
        scrollSelectionIntoView(win);
        iframe.scrollIntoView({ block: 'center', inline: 'nearest' });
        return;
      }
    }

    const { top, bottom, left, right } = box;

    const iframeRect = iframe.getBoundingClientRect();
    const insetTop = iframe.clientTop;
    const insetLeft = iframe.clientLeft;
    const caretTopInHostViewport = iframeRect.top + insetTop + top;
    const caretBottomInHostViewport = iframeRect.top + insetTop + bottom;
    const caretMidYInHostViewport = (caretTopInHostViewport + caretBottomInHostViewport) / 2;
    const caretMidXInHostViewport = iframeRect.left + insetLeft + (left + right) / 2;

    const scrollRoot = findHostScrollContainer(iframe.parentElement);
    if (!scrollRoot) {
      iframe.scrollIntoView({ block: 'center', inline: 'nearest' });
      return;
    }

    const rootRect = scrollRoot.getBoundingClientRect();
    const visibleMidY = rootRect.top + rootRect.height / 2;
    scrollRoot.scrollTop += caretMidYInHostViewport - visibleMidY;
    const visibleMidX = rootRect.left + rootRect.width / 2;
    scrollRoot.scrollLeft += caretMidXInHostViewport - visibleMidX;
  } catch {
    try {
      iframe.scrollIntoView({ block: 'center', inline: 'nearest' });
    } catch {
      /* ignore */
    }
  }
}

// ---------------------------------------------------------------------------
// Page creation
// ---------------------------------------------------------------------------

const PAGE_DIM_STYLE_PROPS = ['width', 'height', 'minHeight', 'maxHeight', 'minWidth', 'maxWidth'];

/**
 * Copy a source page's class and page-affecting inline styles onto a newly
 * created page div. Must include the `page` CSS property (not just dimensions)
 * because that's where the landscape-page marker lives
 * (`style="page: landscape-page;"` — see buildLandscapePage in export.util.ts).
 * The PDF export service splits portrait/landscape purely off that marker, so
 * a page split that only copies dimensions silently turns the new page portrait
 * in the exported PDF even though it still looks landscape in this editor.
 */
function copyPageStyling(sourcePage: HTMLElement, target: HTMLElement): void {
  PAGE_DIM_STYLE_PROPS.forEach(prop => {
    const v = (sourcePage.style as unknown as Record<string, string>)[prop];
    if (v) (target.style as unknown as Record<string, string>)[prop] = v;
  });
  const page = (sourcePage.style as unknown as Record<string, string>).page;
  if (page) (target.style as unknown as Record<string, string>).page = page;
}

/**
 * Insert a blank duplicate page directly after `sourcePage`, copying its class
 * and inline dimensions. A minimal editable stub (`<p><br></p>`) is added so
 * the caret has somewhere to land.
 */
export function insertBlankDuplicatePageAfter(sourcePage: HTMLElement, doc: Document): HTMLElement {
  const np = doc.createElement('div');
  np.className = sourcePage.className;
  copyPageStyling(sourcePage, np);

  const stub = doc.createElement('p');
  stub.innerHTML = '<br>';
  np.appendChild(stub);

  sourcePage.after(np);
  return np;
}

/**
 * Split any `.page` element whose rendered content exceeds its CSS min-height
 * (i.e. the page overflows). Overflow nodes are moved to a new page inserted
 * immediately after the current one, preserving class / inline dimensions.
 * Iterates until no page overflows (a newly-created page can also overflow).
 *
 * Tables are never torn apart: if a table is the first content child on a page
 * and the page still overflows, the whole table is moved to the next page rather
 * than splitting inside it.
 *
 * Returns the number of page splits performed.
 */
/**
 * Returns the cumulative offsetTop of `el` relative to `ancestor` by walking
 * the offsetParent chain. Does not include `el`'s own height.
 */
function offsetTopRelativeTo(el: HTMLElement, ancestor: HTMLElement): number {
  let top = 0;
  let cur: HTMLElement | null = el;
  while (cur && cur !== ancestor) {
    top += cur.offsetTop;
    cur = cur.offsetParent as HTMLElement | null;
  }
  return top;
}

/**
 * Find the split point inside `container` whose children need to be moved to
 * a new page. `cutoff` is the maximum allowed bottom offset relative to `page`.
 * Returns { container, splitAtIndex } where `splitAtIndex` is the first child
 * index inside `container` that pushes past `cutoff`, or null if no split found.
 *
 * Recurses into a single oversized child wrapper div so content nested inside
 * one wrapper div is also split correctly.
 */
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const HEADING_CLASSES = [
  'major-section-header',
  'section-header',
  'section-subheader',
  'budget-title',
  'continuation-title',
  'photos-title',
  'recap-subtitle',
  'italic-subheader',
];
function isHeadingLike(el: HTMLElement): boolean {
  return HEADING_TAGS.has(el.tagName) || HEADING_CLASSES.some(cls => el.classList.contains(cls));
}

/**
 * splitAtIndex === -1 is a sentinel meaning "row-split the table that is
 * `container` itself" — used when a table is the first/only overflowing child
 * and it supports row-level splitting (no `no-row-split` class).
 */
function findSplitPoint(
  container: HTMLElement,
  page: HTMLElement,
  cutoff: number,
): { container: HTMLElement; splitAtIndex: number } | null {
  const kids = Array.from(container.children) as HTMLElement[];
  // Filter out page chrome if container is the page itself
  const filtered = container === page ? (contentChildren(page) as HTMLElement[]) : kids;

  for (let i = 0; i < filtered.length; i++) {
    const child = filtered[i];
    const childTop = offsetTopRelativeTo(child, page);
    const childBottom = childTop + child.offsetHeight;

    if (childBottom > cutoff + 1) {
      // This child overflows. If it's the first child in this container we
      // can't split before it at this level.
      if (i === 0) {
        // Splittable table (no no-row-split class) — signal row-split via sentinel.
        if (child.tagName === 'TABLE' && !child.classList.contains('no-row-split')) {
          return { container: child, splitAtIndex: -1 };
        }
        // Splittable list (e.g. action items) — signal item-split via the same sentinel.
        if ((child.tagName === 'OL' || child.tagName === 'UL') && !child.classList.contains('no-item-split')) {
          return { container: child, splitAtIndex: -1 };
        }
        // Non-table/non-list: recurse into it if possible.
        if (child.tagName !== 'TABLE' && child.tagName !== 'OL' && child.tagName !== 'UL' && child.children.length > 0) {
          return findSplitPoint(child, page, cutoff);
        }
        // no-row-split table, no-item-split list, or leaf with no children — no split possible here.
        return null;
      }

      // If the overflowing child is a splittable table, row-split it in place
      // (keep preceding content on this page, split table rows across pages).
      if (child.tagName === 'TABLE' && !child.classList.contains('no-row-split')) {
        return { container: child, splitAtIndex: -1 };
      }

      // If the overflowing child is a splittable list, item-split it in place
      // (keep preceding list items on this page, continue the rest on the next page).
      if ((child.tagName === 'OL' || child.tagName === 'UL') && !child.classList.contains('no-item-split')) {
        return { container: child, splitAtIndex: -1 };
      }

      // If the overflowing child is a wrapper div (e.g. break-inside:avoid section wrapper),
      // recurse into it to find a splittable table inside before moving the whole div.
      if (child.tagName === 'DIV' && !isHeadingLike(child) && child.children.length > 0) {
        const inner = findSplitPoint(child, page, cutoff);
        if (inner !== null) {
          return inner;
        }
      }

      // Walk back past any heading-like elements so they move to the new page
      // with their following content rather than being stranded alone.
      // Stop at 1 minimum — we must leave at least one element on the current page.
      let splitAt = i;
      while (splitAt > 1 && isHeadingLike(filtered[splitAt - 1])) {
        splitAt--;
      }

      // Special case: the heading walk stopped at splitAt=1 because filtered[0] is a heading.
      // Rather than orphaning the heading, try to recurse into filtered[1] to find a split
      // point inside it. If recursion fails, return null — better to overflow than strand a heading alone.
      if (splitAt === 1 && isHeadingLike(filtered[0])) {
        const sibling = filtered[1];
        if (sibling?.tagName !== 'TABLE' && sibling?.children.length > 0) {
          const inner = findSplitPoint(sibling, page, cutoff);
          if (inner !== null) return inner;
        }
        return null;
      }

      return { container, splitAtIndex: splitAt };
    }
  }
  return null;
}

/**
 * Split a `.page.continuation` whose `table.line-items` overflows.
 * Moves excess `<tbody>` rows to a new continuation page that carries a
 * cloned `<thead>` so column headers repeat. Returns true if a split occurred.
 */
function splitContinuationTablePage(page: HTMLElement, doc: Document, contentCutoff: number): boolean {
  const table = page.querySelector('table.line-items') as HTMLTableElement | null;
  if (!table) return false;

  const colgroup = table.querySelector('colgroup');
  const thead = table.querySelector('thead');
  const tbody = table.querySelector('tbody');
  if (!tbody) return false;

  const rows = Array.from(tbody.rows) as HTMLTableRowElement[];
  // Need at least 2 rows to split (keep at least 1 on current page).
  if (rows.length < 2) return false;

  // Use getBoundingClientRect relative to the page top edge so flex/table layout
  // doesn't confuse the measurement (offsetTop walks offsetParent, which breaks inside flex).
  // rowBottomFromPageTop includes padding-top, so compare against targetHeight - paddingBottom.
  const pageRect = page.getBoundingClientRect();
  const style = doc.defaultView?.getComputedStyle(page);
  const paddingTop = parseFloat(style?.paddingTop ?? '0') || 0;
  const paddingBottom = parseFloat(style?.paddingBottom ?? '0') || 0;
  const targetHeight = paddingTop + contentCutoff + paddingBottom;
  const cutoffFromPageTop = targetHeight - paddingBottom;

  let splitAtRow = -1;
  for (let i = 1; i < rows.length; i++) {
    const rowBottomFromPageTop = rows[i].getBoundingClientRect().bottom - pageRect.top;
    if (rowBottomFromPageTop > cutoffFromPageTop + 1) {
      splitAtRow = i;
      break;
    }
  }
  if (splitAtRow < 0) return false;

  // If the totals row itself is the first overflow row, pull the split back one
  // so at least one data row accompanies it on the new page.
  if (splitAtRow > 1 && rows[splitAtRow].classList.contains('totals-row')) {
    splitAtRow--;
  }

  // Build new continuation page mirroring the current one.
  const newPage = doc.createElement('div');
  newPage.className = page.className;
  copyPageStyling(page, newPage);

  // Clone the title element if present so it repeats on every continuation page.
  const titleEl = page.querySelector('.continuation-title') as HTMLElement | null;
  if (titleEl) {
    newPage.appendChild(titleEl.cloneNode(true));
  }

  // Build a new table with the original thead + overflow rows.
  const newTable = doc.createElement('table');
  newTable.className = table.className;
  if (table.style.cssText) newTable.style.cssText = table.style.cssText;
  // The colgroup carries the fixed column widths; without it the continuation table re-auto-sizes.
  if (colgroup) newTable.appendChild(colgroup.cloneNode(true));
  if (thead) newTable.appendChild(thead.cloneNode(true));
  const newTbody = doc.createElement('tbody');
  const overflowRows = rows.slice(splitAtRow);
  overflowRows.forEach(r => newTbody.appendChild(r));
  newTable.appendChild(newTbody);
  newPage.appendChild(newTable);

  page.after(newPage);
  return true;
}

/**
 * Split a regular (portrait, non-continuation) page whose content overflows because
 * a table is too tall. Splits the table row-by-row: rows that exceed `contentCutoff`
 * move to a new page, with the thead cloned so column headers repeat.
 * Tables with class `no-row-split` are moved wholesale to the next page instead.
 * Returns true if a split occurred.
 */
function splitOverflowingTableOnPage(page: HTMLElement, table: HTMLElement, doc: Document, contentCutoff: number): boolean {
  // no-row-split tables move whole to next page — handled by caller via findSplitPoint.
  if (table.classList.contains('no-row-split')) return false;

  const tableEl = table as HTMLTableElement;
  const colgroup = tableEl.querySelector('colgroup');
  const thead = tableEl.querySelector('thead');
  const tbody = tableEl.querySelector('tbody');
  const tfoot = tableEl.querySelector('tfoot');

  // Collect all splittable rows: tbody rows + tfoot rows (treated as trailing rows).
  const tbodyRows = tbody ? (Array.from(tbody.rows) as HTMLTableRowElement[]) : [];
  const tfootRows = tfoot ? (Array.from(tfoot.rows) as HTMLTableRowElement[]) : [];
  const allRows = [...tbodyRows, ...tfootRows];
  if (allRows.length < 1) return false;

  const pageRect = page.getBoundingClientRect();
  const style = doc.defaultView?.getComputedStyle(page);
  const paddingTop = parseFloat(style?.paddingTop ?? '0') || 0;
  const paddingBottom = parseFloat(style?.paddingBottom ?? '0') || 0;
  const targetHeight = paddingTop + contentCutoff + paddingBottom;
  const cutoffFromPageTop = targetHeight - paddingBottom;

  // Normally keep at least 1 row on the current page (start scanning at index 1). When only a
  // single row remains (e.g. after an earlier split already moved the rest), that row is the
  // whole table's content — if it alone overflows, it must still move on to the next page rather
  // than leaving the page stuck overflowing forever with nothing left to split.
  let splitAtRow = -1;
  const scanStart = allRows.length === 1 ? 0 : 1;
  for (let i = scanStart; i < allRows.length; i++) {
    const rowBottomFromPageTop = allRows[i].getBoundingClientRect().bottom - pageRect.top;
    if (rowBottomFromPageTop > cutoffFromPageTop + 1) {
      splitAtRow = i;
      break;
    }
  }
  if (splitAtRow < 0) return false;

  // Build a new page with the same class/style as the current one.
  const newPage = doc.createElement('div');
  newPage.className = page.className;
  copyPageStyling(page, newPage);

  // Move overflow rows to new table. Rows that belong to tfoot stay in a tfoot on the new page.
  const overflowRows = allRows.slice(splitAtRow);
  const newTable = doc.createElement('table');
  newTable.className = tableEl.className;
  if (tableEl.style.cssText) newTable.style.cssText = tableEl.style.cssText;
  // The colgroup carries the fixed column widths; without it the continuation table re-auto-sizes.
  if (colgroup) newTable.appendChild(colgroup.cloneNode(true));
  if (thead) newTable.appendChild(thead.cloneNode(true));
  const newTbody = doc.createElement('tbody');
  overflowRows.forEach(r => {
    if (tfoot && tfoot.contains(r)) return; // tfoot rows handled below
    newTbody.appendChild(r);
  });
  if (newTbody.rows.length > 0) newTable.appendChild(newTbody);
  // If tfoot overflows, move the whole tfoot to the new page.
  if (tfoot && overflowRows.some(r => tfoot.contains(r))) {
    newTable.appendChild(tfoot);
  }
  newPage.appendChild(newTable);

  // Move siblings after the table (within its parent) to the new page.
  let sib = table.nextElementSibling as HTMLElement | null;
  while (sib) {
    const next = sib.nextElementSibling as HTMLElement | null;
    if (!isHelperNode(sib)) newPage.appendChild(sib);
    sib = next;
  }

  // Also move page-level siblings after the table's direct-child-of-page ancestor,
  // so content in subsequent sections at page root also flows to the new page.
  let ancestor: HTMLElement | null = table.parentElement;
  while (ancestor && ancestor.parentElement !== page) {
    ancestor = ancestor.parentElement;
  }
  if (ancestor && ancestor !== table) {
    let pageSib = ancestor.nextElementSibling as HTMLElement | null;
    while (pageSib) {
      const next = pageSib.nextElementSibling as HTMLElement | null;
      if (!isHelperNode(pageSib)) newPage.appendChild(pageSib);
      pageSib = next;
    }
  }

  page.after(newPage);
  return true;
}

/**
 * Split a page whose overflowing element is a single `<li>` inside an action-items list,
 * where the `<li>` itself contains a table too tall to fit (e.g. a Change Orders table
 * bundled into one action-items bullet). Row-splits that table in place: the `<li>`'s
 * heading/intro content and the rows that fit stay on the current page; a continuation
 * `<li>` on a new page carries the overflow rows (thead cloned so headers repeat) plus
 * whatever else was in the original `<li>` after the table, followed by any later `<li>`s
 * and page-level siblings. Returns true if a split occurred.
 */
function splitOverflowingListItemTable(
  page: HTMLElement,
  list: HTMLElement,
  item: HTMLLIElement,
  table: HTMLTableElement,
  doc: Document,
  contentCutoff: number,
): boolean {
  const colgroup = table.querySelector('colgroup');
  const thead = table.querySelector('thead');
  const tbody = table.querySelector('tbody');
  const tfoot = table.querySelector('tfoot');
  const tbodyRows = tbody ? (Array.from(tbody.rows) as HTMLTableRowElement[]) : [];
  const tfootRows = tfoot ? (Array.from(tfoot.rows) as HTMLTableRowElement[]) : [];
  const allRows = [...tbodyRows, ...tfootRows];
  if (allRows.length < 2) return false;

  const pageRect = page.getBoundingClientRect();
  const style = doc.defaultView?.getComputedStyle(page);
  const paddingTop = parseFloat(style?.paddingTop ?? '0') || 0;
  const paddingBottom = parseFloat(style?.paddingBottom ?? '0') || 0;
  const targetHeight = paddingTop + contentCutoff + paddingBottom;
  const cutoffFromPageTop = targetHeight - paddingBottom;

  let splitAtRow = -1;
  for (let i = 1; i < allRows.length; i++) {
    const rowBottomFromPageTop = allRows[i].getBoundingClientRect().bottom - pageRect.top;
    if (rowBottomFromPageTop > cutoffFromPageTop + 1) {
      splitAtRow = i;
      break;
    }
  }
  if (splitAtRow < 0) return false;

  const newPage = doc.createElement('div');
  newPage.className = `${page.className} list-continuation`.trim();
  copyPageStyling(page, newPage);

  // Continuation table: cloned thead + overflow rows.
  const overflowRows = allRows.slice(splitAtRow);
  const newTable = doc.createElement('table');
  newTable.className = table.className;
  if (table.style.cssText) newTable.style.cssText = table.style.cssText;
  // The colgroup carries the fixed column widths; without it the continuation table re-auto-sizes.
  if (colgroup) newTable.appendChild(colgroup.cloneNode(true));
  if (thead) newTable.appendChild(thead.cloneNode(true));
  const newTbody = doc.createElement('tbody');
  overflowRows.forEach(r => {
    if (tfoot && tfoot.contains(r)) return;
    newTbody.appendChild(r);
  });
  if (newTbody.rows.length > 0) newTable.appendChild(newTbody);
  if (tfoot && overflowRows.some(r => tfoot.contains(r))) {
    newTable.appendChild(tfoot);
  }

  // Continuation <li>: same class as the original (no counter-increment here — this bullet's
  // number was already assigned on the current page; the continuation is a visual carry-over,
  // not a new numbered item), holding the split table plus anything after the table in the
  // original <li>.
  const newItem = doc.createElement('li');
  newItem.className = item.className;
  if (item.style.cssText) newItem.style.cssText = item.style.cssText;
  newItem.style.listStyle = 'none';
  newItem.appendChild(newTable);
  let afterTable = table.nextSibling as ChildNode | null;
  while (afterTable) {
    const next = afterTable.nextSibling as ChildNode | null;
    newItem.appendChild(afterTable);
    afterTable = next;
  }

  // Continuation list + host wrapper, mirroring splitOverflowingListOnPage's structure so
  // `.action-items ol li` numbering/style selectors still match on the new page.
  const listEl = list as HTMLOListElement | HTMLUListElement;
  const priorOffsetMatch = /action-num\s+(-?\d+)/.exec(listEl.style.counterReset || '');
  const priorOffset = priorOffsetMatch ? parseInt(priorOffsetMatch[1], 10) : 0;
  const items = Array.from(listEl.children).filter(el => el.tagName === 'LI') as HTMLLIElement[];
  const itemIndex = items.indexOf(item);
  const newList = doc.createElement(listEl.tagName.toLowerCase());
  newList.className = listEl.className;
  if (listEl.style.cssText) newList.style.cssText = listEl.style.cssText;
  // This bullet already counted as one item on the current page, so the next fresh bullet
  // (if any) on the continuation page must start counting from itemIndex + 1, same as
  // splitOverflowingListOnPage's convention.
  newList.style.counterReset = `action-num ${priorOffset + itemIndex + 1}`;
  newList.appendChild(newItem);

  const wrapper = list.closest('.action-items') as HTMLElement | null;
  let listHost: HTMLElement = newPage;
  if (wrapper) {
    const newWrapper = doc.createElement(wrapper.tagName.toLowerCase());
    newWrapper.className = wrapper.className;
    if (wrapper.style.cssText) newWrapper.style.cssText = wrapper.style.cssText;
    newPage.appendChild(newWrapper);
    listHost = newWrapper;
  }
  listHost.appendChild(newList);

  // Move any later <li>s in the same list to the continuation list too.
  const laterItems = items.slice(itemIndex + 1);
  laterItems.forEach(li => newList.appendChild(li));

  // Move siblings after the list (within its parent) to the new page.
  let sib = list.nextElementSibling as HTMLElement | null;
  while (sib) {
    const next = sib.nextElementSibling as HTMLElement | null;
    if (!isHelperNode(sib)) listHost.appendChild(sib);
    sib = next;
  }

  // Move page-level siblings after the list's direct-child-of-page ancestor.
  let ancestor: HTMLElement | null = list.parentElement;
  while (ancestor && ancestor.parentElement !== page) {
    ancestor = ancestor.parentElement;
  }
  if (ancestor && ancestor !== list) {
    let pageSib = ancestor.nextElementSibling as HTMLElement | null;
    while (pageSib) {
      const next = pageSib.nextElementSibling as HTMLElement | null;
      if (!isHelperNode(pageSib)) newPage.appendChild(pageSib);
      pageSib = next;
    }
  }

  page.after(newPage);
  return true;
}

/**
 * Split a regular (portrait, non-continuation) page whose content overflows because
 * a list (e.g. the action items `<ol>`) is too tall. Splits the list item-by-item:
 * items that exceed `contentCutoff` move to a new page inside a continuation list of
 * the same type. Action items number via a CSS `counter-increment: action-num` on each
 * `<li>` (not the native `<ol>` marker), so a fresh `<ol>` on the new page must seed
 * `counter-reset: action-num <n>` to continue the count — the `start` attribute alone
 * has no effect on that counter.
 * Lists with class `no-item-split` are moved wholesale to the next page instead.
 * Returns true if a split occurred.
 */
function splitOverflowingListOnPage(page: HTMLElement, list: HTMLElement, doc: Document, contentCutoff: number): boolean {
  // no-item-split lists move whole to next page — handled by caller via findSplitPoint.
  if (list.classList.contains('no-item-split')) return false;

  const listEl = list as HTMLOListElement | HTMLUListElement;
  const items = Array.from(listEl.children).filter(el => el.tagName === 'LI') as HTMLLIElement[];
  if (items.length < 1) return false;

  const pageRect = page.getBoundingClientRect();
  const style = doc.defaultView?.getComputedStyle(page);
  const paddingTop = parseFloat(style?.paddingTop ?? '0') || 0;
  const paddingBottom = parseFloat(style?.paddingBottom ?? '0') || 0;
  const targetHeight = paddingTop + contentCutoff + paddingBottom;
  const cutoffFromPageTop = targetHeight - paddingBottom;

  // Normally keep at least 1 item on the current page (start scanning at index 1). When only a
  // single item remains (e.g. after an earlier split already moved the rest), that item is the
  // whole list's content — if it alone overflows, it must still move on rather than leaving the
  // page stuck overflowing forever with nothing left to split.
  let splitAtItem = -1;
  const scanStart = items.length === 1 ? 0 : 1;
  for (let i = scanStart; i < items.length; i++) {
    const itemBottomFromPageTop = items[i].getBoundingClientRect().bottom - pageRect.top;
    if (itemBottomFromPageTop > cutoffFromPageTop + 1) {
      // Before moving this whole <li> to the next page, check whether it contains a
      // splittable table that itself pushes past the cutoff (e.g. a Change Orders table
      // inside an action-items bullet). If so, row-split that table in place — keeping
      // the <li>'s own heading/intro content on this page — rather than relocating the
      // entire bullet (table and all) as one atomic block, which otherwise leaves large
      // unused space on the current page and starts the next page mid-list.
      const nestedTable = items[i].querySelector('table') as HTMLTableElement | null;
      if (nestedTable && !nestedTable.classList.contains('no-row-split')) {
        const tableBottomFromPageTop = nestedTable.getBoundingClientRect().bottom - pageRect.top;
        if (tableBottomFromPageTop > cutoffFromPageTop + 1) {
          const split = splitOverflowingListItemTable(page, list, items[i], nestedTable, doc, contentCutoff);
          if (split) return true;
          // Fall through to the whole-item move below if the table itself couldn't be split
          // (e.g. fewer than 2 rows, or the table isn't what's actually overflowing).
        }
      }
      splitAtItem = i;
      break;
    }
  }
  if (splitAtItem < 0) return false;

  // Build a new page with the same class/style as the current one, tagged with
  // `list-continuation` (distinct from the landscape table `.continuation` class, which
  // routes into a different height/selector-based split pass) so `consolidateContinuationPages`
  // can find and merge it back on a later reload/re-split instead of leaving it stranded.
  const newPage = doc.createElement('div');
  newPage.className = `${page.className} list-continuation`.trim();
  copyPageStyling(page, newPage);

  // Move overflow items to a new list of the same tag/class. Action items number via
  // a CSS counter (`counter-increment: action-num`), which resets to 0 in any new
  // `<ol>`/`<ul>` tree, so seed `counter-reset` here to continue the count. If `list`
  // is itself already a continuation from an earlier split, it carries its own
  // `counter-reset` offset — add `splitAtItem` (items staying behind here) on top of
  // that so a second/third split keeps numbering contiguous instead of resetting.
  const priorOffsetMatch = /action-num\s+(-?\d+)/.exec(listEl.style.counterReset || '');
  const priorOffset = priorOffsetMatch ? parseInt(priorOffsetMatch[1], 10) : 0;
  const overflowItems = items.slice(splitAtItem);
  const newList = doc.createElement(listEl.tagName.toLowerCase());
  newList.className = listEl.className;
  if (listEl.style.cssText) newList.style.cssText = listEl.style.cssText;
  newList.style.counterReset = `action-num ${priorOffset + splitAtItem}`;
  overflowItems.forEach(li => newList.appendChild(li));

  // The action-items numbering/style rules are scoped by CSS as `.action-items ol li`,
  // so the moved list must stay inside an `.action-items` wrapper on the new page too —
  // otherwise it silently falls back to plain browser list numbering (1, 2, 3...).
  const wrapper = list.closest('.action-items') as HTMLElement | null;
  let listHost: HTMLElement = newPage;
  if (wrapper) {
    const newWrapper = doc.createElement(wrapper.tagName.toLowerCase());
    newWrapper.className = wrapper.className;
    if (wrapper.style.cssText) newWrapper.style.cssText = wrapper.style.cssText;
    newPage.appendChild(newWrapper);
    listHost = newWrapper;
  }
  listHost.appendChild(newList);

  // Move siblings after the list (within its parent) to the new page — into the same
  // wrapper as the list itself, if any, to preserve the original nesting.
  let sib = list.nextElementSibling as HTMLElement | null;
  while (sib) {
    const next = sib.nextElementSibling as HTMLElement | null;
    if (!isHelperNode(sib)) listHost.appendChild(sib);
    sib = next;
  }

  // Also move page-level siblings after the list's direct-child-of-page ancestor,
  // so content in subsequent sections at page root also flows to the new page.
  let ancestor: HTMLElement | null = list.parentElement;
  while (ancestor && ancestor.parentElement !== page) {
    ancestor = ancestor.parentElement;
  }
  if (ancestor && ancestor !== list) {
    let pageSib = ancestor.nextElementSibling as HTMLElement | null;
    while (pageSib) {
      const next = pageSib.nextElementSibling as HTMLElement | null;
      if (!isHelperNode(pageSib)) newPage.appendChild(pageSib);
      pageSib = next;
    }
  }

  page.after(newPage);
  return true;
}

/**
 * Merge a run of consecutive continuation pages (all matched by the same selector, passed
 * in as `runPages`) back into the page that owns the split content.
 *
 * When `firstIsContinuation` is true, the first page of a run is itself part of `runPages`
 * (e.g. the pre-baked landscape `.continuation` sheet — see buildContinuationPage in
 * export.util.ts). When false, `runPages` holds only overflow pages (e.g. `.list-continuation`)
 * and the merge target is the plain page immediately preceding the run.
 */
function mergeContinuationRuns(runPages: HTMLElement[], firstIsContinuation: boolean): void {
  if (runPages.length === 0) return;

  let i = 0;
  while (i < runPages.length) {
    let first: HTMLElement | null;
    let j: number;
    if (firstIsContinuation) {
      first = runPages[i];
      j = i + 1;
    } else {
      first = getPreviousPageElement(runPages[i]) as HTMLElement | null;
      j = i;
    }
    if (!first) {
      i++;
      continue;
    }

    const firstTable = first.querySelector('table.line-items') as HTMLTableElement | null;
    const firstTbody = firstTable?.querySelector('tbody');
    const firstList = !firstTbody ? (first.querySelector('.action-items > ol, .action-items > ul') as HTMLElement | null) : null;
    if (!firstTbody && !firstList) {
      i++;
      continue;
    }

    while (j < runPages.length) {
      const next = runPages[j];
      // Only merge if it's the immediately following .page sibling (skip separators).
      // Compare against the expected preceding page by array identity (not a fresh DOM
      // walk from `next`'s current position), since earlier iterations may have already
      // removed that preceding page from the document.
      const expectedPrev = j === i ? first : runPages[j - 1];
      const prevPage = getPreviousPageElement(next);
      if (prevPage !== expectedPrev) break;

      // Only merge (and remove) `next` when it actually has a matching structure to pull
      // content from — otherwise stop this run and leave `next` (and its content) intact
      // rather than discarding an incompatible page.
      let merged = false;
      if (firstTbody) {
        const nextTbody = next.querySelector('tbody');
        if (nextTbody) {
          Array.from(nextTbody.rows).forEach(r => firstTbody.appendChild(r));
          merged = true;
        }
      } else if (firstList) {
        const nextList = next.querySelector('.action-items > ol, .action-items > ul');
        if (nextList) {
          Array.from(nextList.children).forEach(li => firstList.appendChild(li));
          merged = true;
        }
      }
      if (!merged) break;
      next.remove();
      j++;
    }
    i = j;
  }
}

/**
 * Merge all consecutive `.page.continuation` (landscape line-items table) pages, and all
 * `.page.list-continuation` (action-items list overflow) pages, back into the page that
 * originally owned the split content. Called before splitting so we always start from a
 * single unsplit page, even when the HTML was previously split and saved back.
 *
 * The two continuation kinds have different "first page" semantics:
 * - `.continuation`: the pre-baked landscape continuation sheet is itself `.continuation`
 *   (see buildContinuationPage in export.util.ts), so the first page of a run is included
 *   in the `.page.continuation` selector.
 * - `.list-continuation`: only holds overflow items; the original list lives on a plain
 *   (non-continuation) `.page`, which precedes the run.
 */
function consolidateContinuationPages(doc: Document): void {
  mergeContinuationRuns(Array.from(doc.querySelectorAll('.page.continuation')) as HTMLElement[], /* firstIsContinuation */ true);
  mergeContinuationRuns(Array.from(doc.querySelectorAll('.page.list-continuation')) as HTMLElement[], /* firstIsContinuation */ false);

  // Continuation lists carry a `counter-reset` offset from the split that created them;
  // once merged back into the first list, that offset is stale and must be cleared so
  // the CSS counter starts fresh from item 1 again.
  doc.querySelectorAll('.action-items > ol, .action-items > ul').forEach(list => {
    (list as HTMLElement).style.counterReset = '';
  });
}

export function splitOverflowingPages(doc: Document, win: Window): number {
  let splits = 0;

  // Merge any previously-split continuation pages back to one before re-splitting,
  // so saving the edited HTML and reloading doesn't double-split.
  consolidateContinuationPages(doc);

  // Continuation pages: single pass only. Row measurements rely on live browser
  // layout; running multiple passes before a reflow produces stale scrollHeight
  // readings on newly-inserted pages and causes over-splitting.
  const continuationPages = Array.from(doc.querySelectorAll('.page.continuation')) as HTMLElement[];
  for (const page of continuationPages) {
    const style = win.getComputedStyle(page);
    const targetHeight = parseFloat(style.height);
    if (!Number.isFinite(targetHeight) || targetHeight <= 0) continue;
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const contentCutoff = targetHeight - paddingTop - paddingBottom;
    const prevOverflow = page.style.overflow;
    page.style.overflow = 'visible';
    const overflows = page.scrollHeight > Math.ceil(targetHeight) + 1;
    page.style.overflow = prevOverflow;
    if (!overflows) continue;
    if (splitContinuationTablePage(page, doc, contentCutoff)) splits++;
  }

  let changed = true;

  while (changed) {
    changed = false;
    const pages = Array.from(doc.querySelectorAll('.page:not(.continuation)')) as HTMLElement[];

    for (const page of pages) {
      const style = win.getComputedStyle(page);
      const targetHeight = parseFloat(style.minHeight);
      if (!Number.isFinite(targetHeight) || targetHeight <= 0) continue;
      if (page.scrollHeight <= Math.ceil(targetHeight) + 1) continue;

      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      const contentCutoff = targetHeight - paddingTop - paddingBottom;

      const split = findSplitPoint(page, page, contentCutoff);
      if (!split) continue;

      // Sentinel: row-split a table, or item-split a list, that is itself the overflowing element.
      if (split.splitAtIndex === -1) {
        const isList = split.container.tagName === 'OL' || split.container.tagName === 'UL';
        const didSplit = isList
          ? splitOverflowingListOnPage(page, split.container, doc, contentCutoff)
          : splitOverflowingTableOnPage(page, split.container, doc, contentCutoff);
        if (didSplit) {
          splits++;
          changed = true;
          break;
        }
        continue;
      }

      const { container, splitAtIndex } = split;
      const containerKids = Array.from(container.children) as HTMLElement[];
      const overflowNodes = containerKids.slice(splitAtIndex);

      // Build a mirrored wrapper path from the split container up to the page,
      // so overflow nodes land inside structurally equivalent wrapper divs on
      // the new page rather than being hoisted to page root.
      //
      // Walk from split container up to page, collecting the ancestor chain.
      const ancestors: HTMLElement[] = [];
      let cur: HTMLElement | null = container;
      while (cur && cur !== page) {
        ancestors.push(cur);
        cur = cur.parentElement as HTMLElement | null;
      }
      // ancestors[0] = split container, ancestors[last] = direct child of page

      const newPage = doc.createElement('div');
      newPage.className = page.className;
      copyPageStyling(page, newPage);

      // Re-create wrapper divs on new page mirroring the ancestor structure,
      // but only cloning tag+className (not content).
      let insertTarget: HTMLElement = newPage;
      for (let a = ancestors.length - 1; a >= 0; a--) {
        const wrapper = doc.createElement(ancestors[a].tagName);
        wrapper.className = ancestors[a].className;
        insertTarget.appendChild(wrapper);
        insertTarget = wrapper;
      }

      // Move overflow nodes into the deepest wrapper on the new page.
      overflowNodes.forEach(n => insertTarget.appendChild(n));

      // Any direct content children of page that come after the top-level
      // ancestor of the split container also move to the new page.
      if (ancestors.length > 0) {
        const topAncestor = ancestors[ancestors.length - 1];
        let sib = topAncestor.nextElementSibling as HTMLElement | null;
        while (sib) {
          const next = sib.nextElementSibling as HTMLElement | null;
          if (!isHelperNode(sib)) newPage.appendChild(sib);
          sib = next;
        }
      }

      if (
        (contentChildren(newPage) as HTMLElement[]).every(n => n.textContent?.trim() === '' && n.querySelectorAll('img,table').length === 0)
      ) {
        const stub = doc.createElement('p');
        stub.innerHTML = '<br>';
        newPage.insertBefore(stub, newPage.firstChild);
      }

      page.after(newPage);
      splits++;
      changed = true;
      break;
    }
  }

  return splits;
}

// ---------------------------------------------------------------------------
// Caret placement
// ---------------------------------------------------------------------------

export function placeCaretInPageStub(page: Element, doc: Document, win: Window): void {
  const sel = win.getSelection();
  if (!sel) return;
  const kids = contentChildren(page);
  const stub = kids.find(k => k.tagName === 'P');
  if (!stub) return;
  try {
    const r = doc.createRange();
    r.setStart(stub, 0);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  } catch {
    // ignore
  }
}

export function placeCaretForDesignModePage(page: Element, doc: Document, win: Window): void {
  const sel = win.getSelection();
  if (!sel) return;

  const kids = contentChildren(page);

  const firstP = kids.find(k => k.tagName === 'P');
  if (firstP) {
    try {
      const r = doc.createRange();
      r.setStart(firstP, 0);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
      return;
    } catch {
      /* fall through */
    }
  }

  const insideElement = (block: Element): boolean => {
    try {
      const r = doc.createRange();
      let n: Node | null = block;
      for (;;) {
        const fc = n.firstChild;
        if (!fc) {
          r.setStart(n, 0);
          break;
        }
        if (fc.nodeType === Node.TEXT_NODE) {
          r.setStart(fc, 0);
          break;
        }
        if (fc.nodeType !== Node.ELEMENT_NODE) {
          r.setStart(n, 0);
          break;
        }
        const el = fc as Element;
        if (el.tagName === 'BR') {
          r.setStartBefore(el);
          break;
        }
        n = el;
      }
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
      return true;
    } catch {
      return false;
    }
  };

  for (let i = 0; i < kids.length; i++) {
    const el = kids[i];
    if (el.tagName === 'TABLE') {
      const cell = el.querySelector('td, th');
      if (cell && insideElement(cell as Element)) return;
      continue;
    }
    if (insideElement(el)) return;
  }

  try {
    const stub = doc.createElement('p');
    stub.innerHTML = '<br>';
    page.insertBefore(stub, page.firstChild);
    const r = doc.createRange();
    r.setStart(stub, 0);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  } catch {
    placeCaretAtPageContentStart(page, doc, win);
  }
}

export function placeCaretAtPageContentStart(page: Element, doc: Document, win: Window): void {
  const sel = win.getSelection();
  if (!sel) return;
  const fi = getFirstContentChildIndex(page);
  if (fi < 0) return;
  try {
    const r = doc.createRange();
    r.setStart(page, fi);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  } catch {
    // ignore
  }
}

export function placeCaretAtPageContentEnd(page: Element, doc: Document, win: Window): void {
  const sel = win.getSelection();
  if (!sel) return;

  const kids = contentChildren(page);
  if (!kids.length) {
    placeCaretAtPageContentStart(page, doc, win);
    return;
  }

  const lastEl = kids[kids.length - 1];
  let n: Node | null = lastEl;

  while (n && n.lastChild) {
    const c: ChildNode = n.lastChild;
    if (c.nodeType === Node.TEXT_NODE) {
      try {
        const r = doc.createRange();
        r.setStart(c, (c as Text).length);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      } catch {
        // ignore
      }
      return;
    }
    if (c.nodeType === Node.ELEMENT_NODE && isHelperNode(c)) break;
    if (c.nodeType === Node.ELEMENT_NODE) {
      n = c;
      continue;
    }
    break;
  }

  try {
    const r = doc.createRange();
    r.selectNodeContents(lastEl);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);
  } catch {
    placeCaretAtPageContentStart(page, doc, win);
  }
}

// ---------------------------------------------------------------------------
// Page emptiness / overflow / caret boundary checks
// ---------------------------------------------------------------------------

/** True when the page has no meaningful body content (only blank paragraphs, brs, etc.). */
export function isPageEffectivelyEmpty(page: Element): boolean {
  const kids = contentChildren(page);
  if (!kids.length) return true;

  for (const k of kids) {
    if (k.nodeType !== Node.ELEMENT_NODE) continue;
    if ((k as Element).tagName === 'TABLE' || (k as Element).querySelector?.('table, img, hr, svg, iframe')) return false;
  }

  const bodyText = kids
    .map(k => (k.textContent || '').replace(/\u00a0/g, ' ').replace(/\u200B/g, ''))
    .join('')
    .trim();

  return bodyText === '';
}

function getPageTargetHeight(page: Element, win: Window): number {
  const target = parseFloat(win.getComputedStyle(page).minHeight);
  return Number.isFinite(target) ? target : 0;
}

export function isPageOverflowing(page: Element, win: Window): boolean {
  const target = getPageTargetHeight(page, win);
  if (!target || target <= 0) return false;
  return (page as HTMLElement).scrollHeight > Math.ceil(target) + 1;
}

function getFirstContentChildIndex(page: Element): number {
  const nodes = Array.from(page.childNodes);
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.nodeType !== Node.ELEMENT_NODE) return i;
    if (!isHelperNode(node)) return i;
  }
  return -1;
}

function getLastContentBoundary(page: Element): number {
  const nodes = Array.from(page.childNodes);
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i];
    if (node.nodeType !== Node.ELEMENT_NODE) return i + 1;
    if (!isHelperNode(node)) return i + 1;
  }
  return -1;
}

export function isCaretAtPageStart(range: Range, page: Element): boolean {
  if (!range.collapsed) return false;
  const firstIndex = getFirstContentChildIndex(page);
  if (firstIndex < 0) return false;
  const beforeCaret = range.cloneRange();
  beforeCaret.setStart(page, firstIndex);
  return (
    beforeCaret
      .toString()
      .replace(/\u200B/g, '')
      .trim() === ''
  );
}

export function isCaretAtPageEnd(range: Range, page: Element): boolean {
  if (!range.collapsed) return false;
  const endIndex = getLastContentBoundary(page);
  if (endIndex < 0) return false;
  const afterCaret = range.cloneRange();
  afterCaret.setEnd(page, endIndex);
  return (
    afterCaret
      .toString()
      .replace(/\u200B/g, '')
      .trim() === ''
  );
}

/** Traverse ancestors to find which `.page` element a given DOM node lives in. */
export function getNodePage(node: Node, doc: Document): Element | null {
  let current: Node | null = node;
  while (current && current !== doc.documentElement) {
    if ((current as Element).classList?.contains('page')) return current as Element;
    current = (current as Element).parentElement;
  }
  return null;
}

// ---------------------------------------------------------------------------
// MutationObserver filtering
// ---------------------------------------------------------------------------

/** True when a mutation record only touches our `__pg-*` UI chrome, not user content. */
export function mutationRecordIsOnlyPageChrome(m: MutationRecord): boolean {
  if (m.type === 'characterData') return false;
  if (m.type === 'attributes') {
    const cls = typeof (m.target as Element).className === 'string' ? (m.target as Element).className : '';
    return cls.includes('__pg-');
  }
  if (m.type === 'childList') {
    const nodes = Array.from(m.addedNodes).concat(Array.from(m.removedNodes));
    if (!nodes.length) return true;
    return nodes.every(n => {
      if (n.nodeType === Node.TEXT_NODE) return !n.textContent?.replace(/\u200B/g, '').trim();
      if (n.nodeType !== Node.ELEMENT_NODE) return false;
      const cls = typeof (n as Element).className === 'string' ? (n as Element).className : '';
      return cls.includes('__pg-');
    });
  }
  return false;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/**
 * Applies a point-size font via `execCommand('fontSize', '7')` then replaces
 * the legacy `<font size="7">` elements with styled `<span>` elements.
 */
export function applyFontSize(doc: Document, sizePt: number): void {
  doc.execCommand('fontSize', false, '7');
  doc.querySelectorAll('font[size="7"]').forEach(el => {
    const span = doc.createElement('span');
    span.style.fontSize = sizePt + 'pt';
    const computedFamily = (doc.defaultView ?? window).getComputedStyle(el).fontFamily;
    if (computedFamily) span.style.fontFamily = computedFamily;
    span.innerHTML = (el as HTMLElement).innerHTML;
    el.replaceWith(span);
  });
  doc.querySelectorAll<HTMLElement>('span[style*="xxx-large"]').forEach(el => {
    el.style.fontSize = sizePt + 'pt';
  });
}

/**
 * Re-applies a pending text insertion after an undo + new-page creation.
 * Used by the page-overflow handler to replay the keystroke on the new page.
 */
export function reapplyPendingInsertion(doc: Document, pendingInput: PendingInput | null, pendingPaste: PendingPaste | null): void {
  if (pendingPaste && (pendingPaste.html || pendingPaste.text)) {
    if (pendingPaste.html) {
      doc.execCommand('insertHTML', false, pendingPaste.html);
    } else {
      doc.execCommand('insertText', false, pendingPaste.text);
    }
    return;
  }
  if (!pendingInput) return;
  const { inputType, data } = pendingInput;
  if ((inputType === 'insertText' || inputType === 'insertReplacementText') && data) {
    doc.execCommand('insertText', false, data);
    return;
  }
  if (inputType === 'insertLineBreak') {
    doc.execCommand('insertLineBreak', false);
    return;
  }
  if (inputType === 'insertParagraph') {
    doc.execCommand('insertParagraph', false);
    return;
  }
  if (inputType === 'insertFromPaste' && data) doc.execCommand('insertText', false, data);
}

// ---------------------------------------------------------------------------
// Color utilities
// ---------------------------------------------------------------------------

/** Convert a CSS `rgb(r, g, b)` string to a `#rrggbb` hex color. */
export function rgbToHex(rgb: string): string {
  const m = rgb.match(/\d+/g);
  if (!m || m.length < 3) return '#ffffff';
  return (
    '#' +
    m
      .slice(0, 3)
      .map(n => parseInt(n, 10).toString(16).padStart(2, '0'))
      .join('')
  );
}
